import React, { useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import RoomAssetPicker from './RoomAssetPicker';
import RoomDraftAccordion from './RoomDraftAccordion';
import { getRoomAmenityPrefillNote } from './prefillRoomAmenities';
import type { RoomAmenityPrefill } from './prefillRoomAmenities';
import type { RoomAssetRow, RoomSource } from './roomAssetMapping';
import type { FlashOfferRoomDraft, PackageRoomOption } from './utils';

type RoomsStepProperty = Pick<App.Property, 'id' | 'name'> & {
  category?: string | null;
  channel_manager?: string | null;
};

interface Props {
  amenityOptions: Array<string>;
  hasRoomDraftFormError: boolean;
  isBusy: boolean;
  isLoadingRoomDraftForm: boolean;
  isLoadingRoomTypesSchema: boolean;
  onAddAmenityOption: (amenityName: string) => void;
  onAddRoomDraft: () => void;
  onRemoveRoomDraft: (draftId: string) => void;
  onRetryLoadRoomFields: () => void;
  onSelectAssetRoom: (assetId: string, roomKey: string) => void;
  onUpdateRoomDraft: (draftId: string, updates: Partial<FlashOfferRoomDraft>) => void;
  packageRoomOptions: Array<PackageRoomOption>;
  property: RoomsStepProperty | null;
  roomAmenityPrefill: RoomAmenityPrefill | null;
  roomDrafts: Array<FlashOfferRoomDraft>;
  roomAssetRows: Array<RoomAssetRow>;
  roomKeyByAssetId: Record<string, string>;
  roomSourceByAssetId: Record<string, RoomSource>;
  roomTypesFormSchema?: RJSFSchema;
  unmatchedSalesforceRoomNames: Array<string>;
}

export default function RoomsStep({
  amenityOptions,
  hasRoomDraftFormError,
  isBusy,
  isLoadingRoomDraftForm,
  isLoadingRoomTypesSchema,
  onAddAmenityOption,
  onAddRoomDraft,
  onRemoveRoomDraft,
  onRetryLoadRoomFields,
  onSelectAssetRoom,
  onUpdateRoomDraft,
  packageRoomOptions,
  property,
  roomAmenityPrefill,
  roomDrafts,
  roomAssetRows,
  roomKeyByAssetId,
  roomSourceByAssetId,
  roomTypesFormSchema,
  unmatchedSalesforceRoomNames,
}: Props) {
  const [roomDraftDeleteId, setRoomDraftDeleteId] = useState<string | null>(null);
  const hasDeletedAllProposedDrafts = roomDrafts.length === 0 && unmatchedSalesforceRoomNames.length > 0;

  return (
    <>
      <Stack spacing={2}>
        {!property?.id && <Alert severity="error">Select or create a property before creating rooms.</Alert>}

        {property?.id && (
          <>
            {hasDeletedAllProposedDrafts && (
              <Alert severity="info">
                <Typography fontWeight={600}>
                  These Salesforce room names couldn&apos;t be confidently matched to an existing room type:
                </Typography>
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {unmatchedSalesforceRoomNames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </Box>
                <Typography variant="body2">
                  If you don&apos;t create a new room type for them here, choose an existing room for their deal options
                  below.
                </Typography>
              </Alert>
            )}

            {roomDrafts.length > 0 && roomAmenityPrefill?.linkStatus === 'unlinked' && (
              <Alert severity="info">
                <AlertTitle>Room amenities can&apos;t be prefilled yet</AlertTitle>
                This property isn&apos;t linked to its accommodation listing yet. After you submit, use{' '}
                <strong>Sync room amenities</strong> on each room in <strong>Content → Rooms</strong> once it&apos;s
                linked. You can still pick amenities here.
              </Alert>
            )}

            {roomDrafts.length > 0 && roomAmenityPrefill?.linkStatus === 'failed' && (
              <Alert severity="warning">
                Couldn&apos;t check the accommodation listing for room amenities. Pick them here, or sync them later in{' '}
                <strong>Content → Rooms</strong>.
              </Alert>
            )}

            {isLoadingRoomDraftForm && (
              <Alert icon={<CircularProgress size={16} />} severity="info">
                Loading room fields.
              </Alert>
            )}

            {hasRoomDraftFormError && (
              <Alert
                severity="error"
                action={
                  <Button disabled={isBusy} onClick={onRetryLoadRoomFields}>
                    Try again
                  </Button>
                }
              >
                Unable to load room fields.
              </Alert>
            )}

            {roomDrafts.map((roomDraft, index) => (
              <RoomDraftAccordion
                key={roomDraft.draftId}
                amenityOptions={amenityOptions}
                amenityPrefillNote={getRoomAmenityPrefillNote(roomAmenityPrefill, roomDraft.draftId)}
                index={index}
                isBusy={isBusy}
                onAddAmenityOption={onAddAmenityOption}
                onRequestRemove={setRoomDraftDeleteId}
                onUpdateRoomDraft={onUpdateRoomDraft}
                property={property}
                roomDraft={roomDraft}
                roomTypesFormSchema={roomTypesFormSchema}
              />
            ))}

            <Box>
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={onAddRoomDraft}
                disabled={isBusy || isLoadingRoomTypesSchema}
              >
                Add room
              </Button>
            </Box>

            <RoomAssetPicker
              disabled={isBusy}
              onSelectRoom={onSelectAssetRoom}
              roomAssetRows={roomAssetRows}
              roomOptions={packageRoomOptions}
              selectedRoomKeyByAssetId={roomKeyByAssetId}
              selectedRoomSourceByAssetId={roomSourceByAssetId}
            />
          </>
        )}
      </Stack>

      <Dialog open={!!roomDraftDeleteId} onClose={() => !isBusy && setRoomDraftDeleteId(null)}>
        <DialogTitle>Remove room draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the draft from this setup flow. It does not delete an existing room.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isBusy} onClick={() => setRoomDraftDeleteId(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={() => {
              if (roomDraftDeleteId) {
                onRemoveRoomDraft(roomDraftDeleteId);
                setRoomDraftDeleteId(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
